import { LEGAL_ROOT_SLUGS } from '../constants/legalRouteSlugs.constants'
import type {
  LegalDocumentVariant,
  InAppLegalDocumentVariant,
} from '../types/legalDocument.types'

/**
 * A Map, not an object literal: the slug comes from the URL, and an object lookup would
 * resolve inherited keys ('constructor', 'toString') to Object.prototype members instead
 * of null, turning `/constructor` into a rendered page.
 */
const SLUG_TO_VARIANT = new Map<string, LegalDocumentVariant>([
  [LEGAL_ROOT_SLUGS.PRIVACY, 'privacy'],
  [LEGAL_ROOT_SLUGS.DATA_DELETION, 'dataDeletion'],
  [LEGAL_ROOT_SLUGS.TERMS, 'terms'],
  [LEGAL_ROOT_SLUGS.CREDITS, 'credits'],
  [LEGAL_ROOT_SLUGS.ABOUT, 'about'],
  [LEGAL_ROOT_SLUGS.SUPPORT, 'support'],
])

/**
 * Normalizes Expo Router `useLocalSearchParams()` slug (string or string[]).
 */
export function normalizeRootSlugParam(
  value: string | string[] | undefined
): string {
  if (typeof value === 'string') {
    return value
  }
  if (Array.isArray(value) && value.length > 0 && typeof value[0] === 'string') {
    return value[0]
  }
  return ''
}

/**
 * Maps a root path segment to a document variant, or null if the slug is not a document URL.
 */
export function getLegalDocumentVariantForSlug(
  slug: string
): LegalDocumentVariant | null {
  return SLUG_TO_VARIANT.get(slug) ?? null
}

/** Every document slug, for static export (app/[slug].tsx generateStaticParams). */
export function getAllLegalDocumentSlugs(): string[] {
  return Array.from(SLUG_TO_VARIANT.keys())
}

/**
 * Narrows to the variants the in-app Privacy submenu can open. About and Support are
 * public marketing pages with no in-app entry point and no analytics event.
 */
export function isInAppLegalDocumentVariant(
  variant: LegalDocumentVariant
): variant is InAppLegalDocumentVariant {
  return variant !== 'about' && variant !== 'support'
}
